(() => {
  document.documentElement.classList.add('astro-motion-ready');

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  const SECTION_SELECTOR = [
    '.adhero',
    '.astro-features',
    '.ad-how',
    '.ad-faq',
    '.astro-footer'
  ].join(',');

  const STAGE_SELECTORS = [
    '.adhero__layout',
    '.astro-features__inner',
    '.ad-how__inner',
    '.ad-faq__inner',
    '.astro-footer__inner'
  ];

  const SURFACE_SELECTORS = [
    '.adhero__background-image',
    '.ad-how__video',
    '.ad-how__fallback-image',
    '.astro-footer__background-image'
  ];

  const REVEAL_GROUPS = [
    {
      selector: '.adhero__copy',
      effect: 'left',
      hero: true
    },
    {
      selector: '.adhero__media',
      effect: 'scale',
      hero: true
    },

    {
      selector: '.astro-features__heading-wrap',
      effect: 'up'
    },
    {
      selector: '.astro-features__visual',
      effect: 'left',
      mask: true
    },
    {
      selector: '.astro-features__item',
      effect: 'right',
      mask: true,
      stagger: 95
    },

    {
      selector: '.ad-how__intro',
      effect: 'up'
    },
    {
      selector: '.ad-how__step',
      effect: 'pop',
      stagger: 115
    },
    {
      selector: '.ad-how__video-shell',
      effect: 'scale',
      mask: true
    },

    {
      selector: '.ad-faq__intro',
      effect: 'left'
    },
    {
      selector: '.ad-faq__terminal',
      effect: 'right',
      mask: true
    },
    {
      selector: '.ad-faq__item',
      effect: 'up',
      stagger: 75
    },

    {
      selector: '.astro-footer__slogan-wrap',
      effect: 'up'
    },
    {
      selector: '.astro-footer__newsletter',
      effect: 'scale',
      mask: true
    },
    {
      selector: '.astro-footer__bottom',
      effect: 'up'
    }
  ];

  const trackedSections = new Set();
  let revealObserver = null;
  let frameRequested = false;

  const clamp = (value, min = 0, max = 1) => {
    return Math.min(max, Math.max(min, value));
  };

  function getRevealObserver() {
    if (revealObserver || reducedMotion) return revealObserver;

    revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.16,
        rootMargin: '0px 0px -9% 0px'
      }
    );

    return revealObserver;
  }

  function applyRevealEffect(element, group) {
    element.classList.add('astro-reveal');

    if (group.effect && group.effect !== 'up') {
      element.classList.add(`astro-reveal--${group.effect}`);
    }

    if (group.mask) {
      element.classList.add('astro-reveal--mask');
    }
  }

  function prepareRevealGroup(root, group) {
    const elements = Array.from(root.querySelectorAll(group.selector));

    elements.forEach((element, index) => {
      if (element.dataset.astroRevealReady === 'true') return;

      element.dataset.astroRevealReady = 'true';
      applyRevealEffect(element, group);

      if (group.stagger) {
        const cappedIndex = Math.min(index, 8);

        element.style.setProperty(
          '--astro-reveal-delay',
          `${cappedIndex * group.stagger}ms`
        );
      }

      if (reducedMotion) {
        element.classList.add('is-visible');
        return;
      }

      if (group.hero) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            element.classList.add('is-visible');
          });
        });

        return;
      }

      getRevealObserver()?.observe(element);
    });
  }

  function findStage(section) {
    for (const selector of STAGE_SELECTORS) {
      const stage = section.querySelector(selector);

      if (stage) return stage;
    }

    return null;
  }

  function prepareSection(section, index) {
    if (section.dataset.astroSectionReady === 'true') return;

    section.dataset.astroSectionReady = 'true';
    section.classList.add('astro-section-flow');

    const stage = findStage(section);

    if (stage) {
      stage.classList.add('astro-section-stage');
    }

    SURFACE_SELECTORS.forEach((selector) => {
      section
        .querySelectorAll(selector)
        .forEach((surface) => surface.classList.add('astro-scroll-surface'));
    });

    /*
      The hero begins directly below the header, so only later sections
      receive the illuminated handoff line.
    */
    if (index > 0 && !section.querySelector(':scope > .astro-section-bridge')) {
      const bridge = document.createElement('div');

      bridge.className = 'astro-section-bridge';
      bridge.setAttribute('aria-hidden', 'true');
      section.prepend(bridge);
    }

    trackedSections.add(section);
  }

  function collectSections(root = document) {
    const sections = [];

    if (root.matches?.(SECTION_SELECTOR)) {
      sections.push(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach((section) => sections.push(section));

    const uniqueSections = [...new Set(sections)];

    uniqueSections.forEach((section, index) => {
      prepareSection(section, index);
    });
  }

  function updateSectionMotion() {
    frameRequested = false;

    if (reducedMotion) return;

    const viewportHeight = window.innerHeight || 1;

    trackedSections.forEach((section) => {
      if (!section.isConnected) {
        trackedSections.delete(section);
        return;
      }

      const rect = section.getBoundingClientRect();

      if (
        rect.bottom < -viewportHeight ||
        rect.top > viewportHeight * 2
      ) {
        return;
      }

      /*
        ENTER:
        0 when a section is below the viewport.
        1 shortly after it begins entering the viewport.
      */
      const enterRange = Math.max(
        viewportHeight * 0.72,
        Math.min(rect.height * 0.62, viewportHeight)
      );

      const enter = clamp(
        (viewportHeight - rect.top) / enterRange
      );

      /*
        EXIT:
        Begins only when the section is nearly out of view.
        This creates a subtle handoff without making content disappear.
      */
      const exit = clamp(
        (viewportHeight * 0.34 - rect.bottom) /
          (viewportHeight * 0.34)
      );

      const stageY = (1 - enter) * 22 - exit * 8;
      const stageScale = 0.992 + enter * 0.008 - exit * 0.002;
      const stageOpacity = 1;

      const sectionCenter = rect.top + rect.height / 2;
      const centerOffset =
        (sectionCenter - viewportHeight / 2) /
        (viewportHeight + rect.height);

      const backgroundY = clamp(centerOffset, -1, 1) * 18;

      const bridgeProgress = clamp(enter * 1.35);
      const bridgeScale = 0.12 + bridgeProgress * 0.88;
      const bridgeOpacity = bridgeProgress * 0.86;

      section.style.setProperty(
        '--astro-stage-y',
        `${stageY.toFixed(2)}px`
      );

      section.style.setProperty(
        '--astro-stage-scale',
        stageScale.toFixed(4)
      );

      section.style.setProperty(
        '--astro-stage-opacity',
        stageOpacity.toFixed(3)
      );

      section.style.setProperty(
        '--astro-bg-y',
        `${backgroundY.toFixed(2)}px`
      );

      section.style.setProperty(
        '--astro-bridge-scale',
        bridgeScale.toFixed(3)
      );

      section.style.setProperty(
        '--astro-bridge-opacity',
        bridgeOpacity.toFixed(3)
      );
    });
  }

  function requestSectionUpdate() {
    if (frameRequested) return;

    frameRequested = true;
    requestAnimationFrame(updateSectionMotion);
  }

  function initAnimations(root = document) {
    collectSections(root);

    REVEAL_GROUPS.forEach((group) => {
      prepareRevealGroup(root, group);
    });

    requestSectionUpdate();
  }

  function revealInside(target) {
    target
      .querySelectorAll?.('.astro-reveal')
      .forEach((element) => element.classList.add('is-visible'));

    if (target.classList?.contains('astro-reveal')) {
      target.classList.add('is-visible');
    }
  }

  window.addEventListener('scroll', requestSectionUpdate, {
    passive: true
  });

  window.addEventListener('resize', requestSectionUpdate, {
    passive: true
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAnimations(document),
      { once: true }
    );
  } else {
    initAnimations(document);
  }

  /* Shopify theme-editor support. */
  document.addEventListener('shopify:section:load', (event) => {
    initAnimations(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    revealInside(event.target);
    requestSectionUpdate();
  });

  document.addEventListener('shopify:block:select', (event) => {
    revealInside(event.target);
  });
})();
